import React, { useState } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useDataSourceProfile from '@afosto/utils-react/dataSource/useDataSourceProfile';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useOrderFilters from '../../utils/useOrderFilters';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import OrderLineCard, { OrderLineCardSkeleton } from '../../components/OrderLineCard';
import OrderTypeDialog from '../../components/OrderTypeDialog';
import BulkButton from '@afosto/components-react/BulkButton';
import DataGrid from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import Page from '@afosto/app-components-react/Page';
import convertFilterToConstraint from '@afosto/utils-react/datagrid/convertFilterToConstraint';
import createDataGridProfileView from '@afosto/utils-react/datagrid/createDataGridProfileView';
import createDefaultView from '@afosto/app-components-react/DataSource/modules/views/utils';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import updateDataGridProfilePositions from '@afosto/utils-react/datagrid/updateDataGridProfilePositions';
import updateDataGridProfileView from '@afosto/utils-react/datagrid/updateDataGridProfileView';
import { filters as filtersApi } from '@afosto/order/api/Filters';
import { items as itemsApi } from '@afosto/order/api/Items';
import { orders as ordersApi } from '@afosto/order/api/Orders';
import {
  addressFormatter,
  ordersFormatter,
  productImageFormatter,
  productSkuFormatter,
  shipmentsFormatter,
  targetFormatter,
} from './formatters';
import translations from './translations';

const ORDER_LINES_PROFILE_MODEL = 'orderLines';
const ORDER_LINES_PROFILE_PATH = 'odr.orders.orderlines';

const orderLinesContainer = props => <Grid container spacing={2} {...props} />;

const OrderLines = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:orders:read']);
  const history = useHistory();
  const intl = useIntl();
  const sessionManager = useSessionManager();
  const { enqueueSnackbar } = useSnackbar();
  const { isFetchingProfile, views } = useDataSourceProfile({
    model: ORDER_LINES_PROFILE_MODEL,
    profilePath: ORDER_LINES_PROFILE_PATH,
    shouldFetchDefinitions: false,
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [showOrderTypeDialog, setShowOrderTypeDialog] = useState(false);
  const [additionalFilters, additionalProperties, isLoadingOrderFilters] = useOrderFilters();
  const isLoading = isFetchingProfile || isLoadingOrderFilters;

  const defaultFilters = [
    {
      key: 'skus',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.skus`,
        defaultMessage: "SKU's",
        description: 'The filter name for column skus',
      }),
      operators: ['in'],
      type: 'sku',
    },
    {
      key: 'order_ids',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.orderIds`,
        defaultMessage: "Order ID's",
        description: 'The filter name for column order ids',
      }),
      operators: ['in'],
      type: 'order',
    },
    {
      key: 'order_count',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.orderCount`,
        defaultMessage: 'Order count',
        description: 'The filter name for column order count',
      }),
      type: 'number',
      operators: ['in', 'gte', 'lte'],
    },
    {
      key: 'is_unavailable',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.isUnavailable`,
        defaultMessage: 'Unavailable',
        description: 'The filter name for column is unavailable',
      }),
      type: 'boolean',
      operators: ['in'],
    },
    {
      key: 'is_backorder',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.isBackorder`,
        defaultMessage: 'Backorder',
        description: 'The filter name for column is backorder',
      }),
      type: 'boolean',
      operators: ['in'],
    },
    {
      key: 'is_backordered',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.isBackordered`,
        defaultMessage: 'Backordered',
        description: `The filter name for column is backordered`,
      }),
      type: 'boolean',
      operators: ['in'],
    },
    {
      key: 'is_inbound',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.isInbound`,
        defaultMessage: 'Inbound',
        description: `The filter name for column is inbound`,
      }),
      type: 'boolean',
      operators: ['in'],
    },
    {
      key: 'target',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.target`,
        defaultMessage: 'Target',
        description: 'The filter name for column target',
      }),
      operators: ['in'],
      type: 'order',
    },
    {
      key: 'backorder_ids',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.backorders`,
        defaultMessage: 'Backorders',
        description: 'The filter name for column backorders',
      }),
      operators: ['in'],
      type: 'order',
    },
    {
      key: 'backorder_count',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.backorderCount`,
        defaultMessage: 'Backorder count',
        description: 'The filter name for column backorder count',
      }),
      type: 'number',
      operators: ['in', 'gte', 'lte'],
    },
    {
      key: 'shipment_count',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.shipmentCount`,
        defaultMessage: 'Shipment count',
        description: `The filter name for column shipment count`,
      }),
      type: 'number',
      operators: ['in', 'gte', 'lte'],
    },
  ];
  const defaultFilterKeys = defaultFilters.map(defaultFilter => defaultFilter.key);

  const defaultProperties = [
    {
      key: 'description',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.description`,
        defaultMessage: 'Description',
        description: 'The column name for column description',
      }),
    },
    {
      key: 'quantity',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.quantity`,
        defaultMessage: 'Quantity',
        description: 'The column name for column quantity',
      }),
    },
    {
      key: 'orders',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.orders`,
        defaultMessage: 'Orders',
        description: 'The column name for column orders',
      }),
      type: 'orders',
    },
  ];

  const fetchOrderLines = async dataSource =>
    dataSourceRequest(
      dataSource,
      async ({ filters = {} }, requestOptions) => {
        const { activeFilters = [] } = filters;
        const constraints = activeFilters
          .filter(activeFilter => defaultFilterKeys.includes(activeFilter.key))
          .reduce((acc, activeFilter) => [...acc, convertFilterToConstraint(activeFilter)], []);

        const orderFilters = activeFilters
          .filter(activeFilter => !defaultFilterKeys.includes(activeFilter.key))
          .reduce(
            (acc, activeFilter) => [
              ...acc,
              { key: activeFilter?.key, values: activeFilter?.value ?? [] },
            ],
            [],
          );

        return itemsApi.searchGroups(
          {
            constraints,
            filters: orderFilters,
          },
          requestOptions,
        );
      },
      {
        transformData: data =>
          data.reduce((acc, orderLine) => {
            const references = orderLine?.references ?? {};
            const backorders = references.backorders ?? [];
            const orderReferences = references.orders ?? [];
            const [firstOrderReference] = orderReferences;
            const orders = orderReferences.reduce(
              (orderAcc, order) => [...orderAcc, order?.id],
              [],
            );

            const shipments = orderReferences
              .reduce(
                (shipmentsAcc, orderReference) => [
                  ...shipmentsAcc,
                  ...(orderReference?.shipments ?? []),
                ],
                [],
              )
              .map(shipment => shipment.id);

            const filterProperties = (orderLine?.filters ?? []).reduce(
              (filterAcc, filter) => ({ ...filterAcc, [`filter_${filter?.key}`]: filter.values }),
              {},
            );

            const addressId = firstOrderReference?.warehouse?.address_id;

            return [
              ...acc,
              {
                ...filterProperties,
                ...orderLine,
                backorders,
                orders,
                shipments,
                target: references.target,
                address: addressId,
                id: orderLine?.sku + orderLine?.ids?.join('-'),
              },
            ];
          }, []),
      },
    );

  const fetchFilterOptions = async filter => {
    try {
      if (defaultFilterKeys.includes(filter.key)) {
        return [];
      }

      const response = await filtersApi.getFilterValues(filter?.key);
      const { data = [] } = response;

      return data.reduce((acc, value) => [...acc, { label: value, value }], []);
    } catch (err) {
      return [];
    }
  };

  const dataSource = useDataGrid(fetchOrderLines, {
    modules: ['dataProperties', 'filters', 'pagination', 'views', 'selection'],
    gridOptions: {
      columnSelection: true,
      filterSelection: true,
      viewsSelection: true,
      copyGridUrl: true,
    },
    options: {
      dataSourceUrl: true,
      isLoadingData: isLoading,
      storeStateKey: 'order_lines',
    },
    pagination: {
      itemsPerPage: 100,
    },
    dataProperties: {
      fixedProperties: [
        {
          key: 'image',
          name: '',
          type: 'productImage',
        },
        {
          key: 'sku',
          name: intl.formatMessage({
            id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.sku`,
            defaultMessage: 'Sku',
            description: 'The column name for column sku',
          }),
          type: 'productSku',
        },
      ],
      properties: [
        ...defaultProperties,
        {
          key: 'backorders',
          name: intl.formatMessage({
            id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.backorders`,
            defaultMessage: 'Backorders',
            description: 'The column name for column backorders',
          }),
          type: 'orders',
        },
        {
          key: 'shipments',
          name: intl.formatMessage({
            id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.shipments`,
            defaultMessage: 'Shipments',
            description: 'The column name for column shipments',
          }),
          type: 'shipments',
        },
        {
          key: 'target',
          name: intl.formatMessage({
            id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.target`,
            defaultMessage: 'Target',
            description: 'The column name for column target',
          }),
          type: 'target',
        },
        {
          key: 'address',
          name: intl.formatMessage({
            id: `dataGrid.columns.${ORDER_LINES_PROFILE_MODEL}.address`,
            defaultMessage: 'Address',
            description: 'The column name for column address',
          }),
          type: 'address',
        },
        ...additionalProperties,
      ],
    },
    views: {
      canCreateViewForTenant: sessionManager && sessionManager.hasPermissions(['odr:orders:write']),
      defaultView: createDefaultView(intl, {
        properties: defaultProperties,
      }),
      views,
      viewShortcuts: true,
      onAddView: (view, otherViews) =>
        createDataGridProfileView(ORDER_LINES_PROFILE_PATH)(view, { views: otherViews }),
      onUpdateView: view => updateDataGridProfileView(ORDER_LINES_PROFILE_PATH)(view),
      onDeleteView: view => updateDataGridProfileView(ORDER_LINES_PROFILE_PATH)(view),
      onRepositionView: repositionedViews =>
        updateDataGridProfilePositions(ORDER_LINES_PROFILE_PATH)(repositionedViews),
    },
    filters: {
      filters: [...defaultFilters, ...additionalFilters],
      loadFilterOptions: fetchFilterOptions,
    },
    selection: {
      allowSelectAll: false,
      onAddSelection: selected => {
        setSelectedItems(oldItems => {
          const currentItemIds = oldItems.map(item => item.id);
          const newItems = selected.filter(item => !currentItemIds.includes(item.id));

          return [...oldItems, ...newItems];
        });
      },
      onRemoveSelection: selected => {
        const removedIds = selected.map(item => item.id);
        setSelectedItems(oldItems => oldItems.filter(item => !removedIds.includes(item.id)));
      },
    },
  });

  const handleCreateOrder = async type => {
    try {
      const orderResponse = await ordersApi.createStack({
        type,
      });
      const { data: orderData = null } = orderResponse;

      const inboundTypes = ['return', 'cancellation', 'purchase'];
      await itemsApi.addItems(
        orderData?.id,
        selectedItems.map(item => {
          const firstOrder = (item.references?.orders ?? []).find(
            orderReference => !orderReference.is_inbound,
          );

          return {
            sku: item.sku,
            reference: item.reference,
            description: item.description,
            quantity: Math.abs(item.quantity),
            is_inbound: inboundTypes.includes(type),
            is_backorder: type === 'purchase',
            item_ids: item?.ids ?? [],
            ...(firstOrder?.id ? { source_order_id: firstOrder.id } : {}),
          };
        }),
      );

      history.push(`/orders/edit/${orderData.id}`);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.orderNotCreated), {
        variant: 'error',
      });
    }
  };

  const bulkActions = [
    {
      key: 'createOrder',
      render: () => (
        <BulkButton
          key="createOrder"
          variant="outlined"
          size="small"
          onClick={() => setShowOrderTypeDialog(true)}
        >
          {intl.formatMessage(translations.actions.createOrder)}
        </BulkButton>
      ),
    },
  ];

  const renderOrderLine = (orderLine, { SelectionComponent }) => (
    <OrderLineCard orderLine={orderLine} SelectionComponent={SelectionComponent} />
  );

  const rendersOrderLineSkeleton = key => <OrderLineCardSkeleton key={key} />;

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent showActions={false} />
      </Page>
    );
  }

  return (
    <Page title={intl.formatMessage(translations.title)} style={{ flexGrow: 1 }} fullWidth>
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={renderOrderLine}
          renderSkeletonItem={rendersOrderLineSkeleton}
          component={orderLinesContainer}
          bulkActions={bulkActions}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid
          dataSource={dataSource}
          bulkActions={bulkActions}
          formatters={{
            address: addressFormatter,
            orders: ordersFormatter,
            productImage: productImageFormatter,
            productSku: productSkuFormatter,
            target: targetFormatter,
            shipments: shipmentsFormatter,
          }}
        />
      </Hidden>
      <OrderTypeDialog
        isOpen={showOrderTypeDialog}
        onClose={() => setShowOrderTypeDialog(false)}
        onSelect={handleCreateOrder}
        type="purchase"
      />
    </Page>
  );
};

export default OrderLines;
